import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Always-eager: tiny pages shown before auth resolves or used in every session
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import LandingPage from './pages/LandingPage';

// Lazy-loaded: split into per-role chunks so each user only downloads what they need
const LogoPreviewPage    = lazy(() => import('./pages/LogoPreviewPage'));
const PrivacyPolicyPage  = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage   = lazy(() => import('./pages/CookiePolicyPage'));
const LegalDocumentPage  = lazy(() => import('./pages/LegalDocumentPage'));
const DataSecurityPage   = lazy(() => import('./pages/DataSecurityPage'));
const FaqPage            = lazy(() => import('./pages/FaqPage'));

// Super admin
const AdminDashboard      = lazy(() => import('./pages/admin/AdminDashboard'));
const ProvisionSchoolPage = lazy(() => import('./pages/admin/ProvisionSchoolPage'));
const SchoolDetailPage    = lazy(() => import('./pages/admin/SchoolDetailPage'));
const SchoolCyclePage     = lazy(() => import('./pages/admin/SchoolCyclePage'));

// School admin
const SchoolDashboard    = lazy(() => import('./pages/school/SchoolDashboard'));
const StaffRosterPage    = lazy(() => import('./pages/school/StaffRosterPage'));
const StaffFormPage      = lazy(() => import('./pages/school/StaffFormPage'));
const StaffProfilePage   = lazy(() => import('./pages/school/StaffProfilePage'));
const OverviewPage       = lazy(() => import('./pages/school/OverviewPage'));
const SchoolSettingsPage = lazy(() => import('./pages/school/SchoolSettingsPage'));
const PayProgressionPage = lazy(() => import('./pages/school/PayProgressionPage'));

// Shared / all roles
const ReviewsPage     = lazy(() => import('./pages/ReviewsPage'));
const CpdLogPage      = lazy(() => import('./pages/cpd/CpdLogPage'));
const ReviewPage      = lazy(() => import('./pages/reviews/ReviewPage'));
const ReviewHistoryPage = lazy(() => import('./pages/reviews/ReviewHistoryPage'));
const PrintReviewPage = lazy(() => import('./pages/reviews/PrintReviewPage'));
const CyclesPage      = lazy(() => import('./pages/CyclesPage'));
const CycleWizardPage = lazy(() => import('./pages/cycles/CycleWizardPage'));
const DocumentsPage   = lazy(() => import('./pages/DocumentsPage'));
const TodoPage        = lazy(() => import('./pages/TodoPage'));

/** Full-screen spinner shown while a lazy chunk is downloading. */
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
    </div>
  );
}

/** Show landing page to unauthenticated users; redirect authenticated users by role */
function RootRedirect() {
  const { appUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!appUser) return <LandingPage />;

  switch (appUser.role) {
    case 'superAdmin':  return <Navigate to="/admin" replace />;
    case 'schoolAdmin': return <Navigate to="/school" replace />;
    case 'headteacher': return <Navigate to="/todo" replace />;
    default:            return <Navigate to="/todo" replace />;
  }
}

export default function App() {
  const { firebaseUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/data-security" element={<DataSecurityPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route
        path="/login"
        element={firebaseUser ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/logo-preview" element={<LogoPreviewPage />} />

      {/* Legal document viewer — standalone, no sidebar, auth required */}
      {firebaseUser && <Route path="/legal/:doc" element={<LegalDocumentPage />} />}

      {/* Authenticated shell */}
      <Route element={<Layout />}>

        {/* Super admin */}
        <Route element={<ProtectedRoute roles={['superAdmin']} />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/schools/new" element={<ProvisionSchoolPage />} />
        </Route>

        {/* School detail — superAdmin (any school) or schoolAdmin (own school) */}
        <Route element={<ProtectedRoute roles={['superAdmin', 'schoolAdmin']} />}>
          <Route path="/admin/schools/:schoolId" element={<SchoolDetailPage />} />
          <Route path="/admin/schools/:schoolId/cycles/:cycleId" element={<SchoolCyclePage />} />
        </Route>

        {/* School admin */}
        <Route element={<ProtectedRoute roles={['schoolAdmin']} />}>
          <Route path="/school" element={<SchoolDashboard />} />
          <Route path="/school/settings" element={<SchoolSettingsPage />} />
          <Route path="/school/staff" element={<StaffRosterPage />} />
          <Route path="/school/staff/new" element={<StaffFormPage />} />
          <Route path="/school/staff/:userId" element={<StaffFormPage />} />
          <Route path="/school/staff/:userId/profile" element={<StaffProfilePage />} />
          <Route path="/school/pay-progression" element={<PayProgressionPage />} />
        </Route>

        {/* Cycles — school admin + headteacher */}
        <Route element={<ProtectedRoute roles={['schoolAdmin', 'headteacher']} />}>
          <Route path="/cycles" element={<CyclesPage />} />
          <Route path="/cycles/:cycleId/overview" element={<OverviewPage />} />
        </Route>

        {/* Cycle wizard — school admin only */}
        <Route element={<ProtectedRoute roles={['schoolAdmin']} />}>
          <Route path="/cycles/new" element={<CycleWizardPage />} />
        </Route>

        {/* To Do — all school members */}
        <Route
          element={
            <ProtectedRoute
              roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']}
            />
          }
        >
          <Route path="/todo" element={<TodoPage />} />
        </Route>

        {/* Reviews — all school members */}
        <Route
          element={
            <ProtectedRoute
              roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']}
            />
          }
        >
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/reviews/history" element={<ReviewHistoryPage />} />
          <Route path="/reviews/:reviewId" element={<ReviewPage />} />
          <Route path="/reviews/:id/print" element={<PrintReviewPage />} />
        </Route>

        {/* Documents — all school members */}
        <Route
          element={
            <ProtectedRoute
              roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']}
            />
          }
        >
          <Route path="/documents" element={<DocumentsPage />} />
        </Route>

        {/* CPD Log — all school members */}
        <Route
          element={
            <ProtectedRoute
              roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']}
            />
          }
        >
          <Route path="/cpd" element={<CpdLogPage />} />
        </Route>

      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
